import { getRoot } from 'mobx-state-tree'

import { replaceAll } from '~/utilities/string'
import USER_VALIDATIONS from '~/utilities/userValidations'
import { validatePhoneNumber } from '~/services/utility'

export const SEARCH_FORM_KEYS = [
  'fullName',
  'email',
  'phoneNumber',
  'licenseID',
  'DOBString',
  'licenseState',
  'zipCode',
  'trafficTicketNumber',
  'violateDate',
]

/** Shared step 1: your info + license + DOB + license state (SSN moved to final step) */
export const NYDMV_SHARED_STEP1_KEYS = [
  'fullName',
  'email',
  'phoneNumber',
  'licenseID',
  'DOBString',
  'licenseState',
]

/** Final-step field (collected last): last 4 of SSN */
export const NYDMV_STEP3_KEYS = [
  'SSN',
]

/** Submit validation keys for NY: step1 + step3 + licenseBack (from scan) + zip (ticket & date optional) */
export const NYDMV_SUBMIT_KEYS_NY = [
  ...NYDMV_SHARED_STEP1_KEYS,
  ...NYDMV_STEP3_KEYS,
  'licenseBack',
  'zipCode',
]

/** Submit validation keys for non-NY: step1 + step3 + zip + ticket & date */
export const NYDMV_SUBMIT_KEYS_NON_NY = [
  ...NYDMV_SHARED_STEP1_KEYS,
  ...NYDMV_STEP3_KEYS,
  'zipCode',
  'trafficTicketNumber',
  'violateDate',
]

/** All fields to send for NYDMV form (both flows). Send as much as we have. */
export const NYDMV_PAYLOAD_KEYS = [
  ...NYDMV_SHARED_STEP1_KEYS,
  ...NYDMV_STEP3_KEYS,
  'licenseBack',
  'zipCode',
  'trafficTicketNumber',
  'violateDate',
]

export const TRAFFIC_TICKET_KEYS_TO_SAVE = [
  'status',
  'attorney',
  'attorneyPrice',
  'winItPrice',
  'basePrice',
  'platformFee',
  'ticketID',
  'points',
  'locationCourt',
  'locationState',
  'violationName',
  'trafficCourt',
  'trafficCounty',
  'issueDate',
  'hearingDate',
  'Charges'
  // 'trafficRegion'
]

export const getUserSessionToken = self => {
  const rootStore = getRoot(self)
  return rootStore.auth.user.sessionToken
}

export const FETCHER_VALIDATIONS = {
  licenseID: value => {
    const licenseIDLength = value.trim().length
    if (!value || !licenseIDLength || (!value.match(/^[a-zA-Z0-9]*$/) || licenseIDLength < 3)) {
      return 'Please enter a valid license ID.'
    }
    return false // no problems
  },
  licenseBack: value => {
    if (!value || !value.trim().length) {
      return 'Please enter the document number from the back of your license.'
    }
    return false
  },
  SSN: value => {
    const error =
      'The last 4 digits of your social security number are required.'
    if (!value) return error
    const safeLength = value.trim().length
    if (safeLength !== 4) return error
    return false
  },
  firstName: value => {
    const firstNameLength = value.trim().length
    if (!firstNameLength) return 'First and last name are required.'
    if (firstNameLength < 2)
      return 'First name should be at least two characters long.'
    return false // no problems
  },
  lastName: value => {
    const lastNameLength = value.trim().length
    if (!lastNameLength) return 'First and last name are required.'
    if (lastNameLength < 2)
      return 'Last name should be at least two characters long.'
    return false // no problems
  },
  fullName: value => {
    const fullNameParts = value.trim().split(' ')
    const numNameParts = fullNameParts.length
    if (!numNameParts) return 'Name is required.'
    if (numNameParts < 2) return 'First and last name are required.'
    if (numNameParts > 3) return 'Name is too long.'

    // Allow alphabets and accented characters (in the range À through ÿ), but no special characters (!?.-_/|\ etc)
    // Héctor Castañeda => Acceptable
    // Michael! W. Griffin? => Unacceptable
    const nameContainsSpecialCharacters = fullNameParts.some((name) => {
      return !(/^[a-zA-Z\u00C0-\u00FF]*$/.test(name))
    })
    if (nameContainsSpecialCharacters) {
      return "Name cannot contain special characters."
    }

    // Allow for a middle name which can be either an initial or a full name
    // Ex. "John D Smith" || "John Doe Smith"
    const isNamePartTooShort = fullNameParts.find((name, index) => {
      if (index === 0 || index === numNameParts - 1) return name.length < 2
      return false
    })
    if (isNamePartTooShort) {
      return 'First and last name should be at least two characters long.'
    }

    return false
  },
  DOBString: value => {
    const ERROR_STRING = 'Please enter a valid date (MM/DD/YYYY).'
    const cleanValue = replaceAll(value, ' ', '')
    const DOBStringLength = cleanValue.length
    if (!DOBStringLength || DOBStringLength < 10) return ERROR_STRING
    const parseDate = new Date(cleanValue)
    if (isNaN(parseDate)) return ERROR_STRING
    const now = new Date()
    if (parseDate >= now) return ERROR_STRING
    // Oldest (verified) living person check (Jeanne Calment)
    const yearsOfAge = now.getFullYear() - parseDate.getFullYear()
    if (yearsOfAge >= 122) return ERROR_STRING
    return false // no problems
  },
  trafficTicketNumber: value => {
    const ticketIDLength = value.trim().length
    if (!value || !ticketIDLength) return 'A ticket ID number is required.'
    if (!value.match(/^\w{5,20}$/)) {
      return 'A ticket ID must be 5 to 20 alphanumeric characters.'
    }
    return false // no problems
  },
  violateDate: value => {
    const ERROR_STRING = 'Please enter a valid date (MM/DD/YYYY).'
    const cleanValue = replaceAll(value, ' ', '')
    const violateDateLength = cleanValue.length
    if (!violateDateLength || violateDateLength < 10) return ERROR_STRING
    const parseDate = new Date(cleanValue)
    if (isNaN(parseDate)) return ERROR_STRING
    const now = new Date()
    if (parseDate > now) return 'Violation date cannot be in the future.'
    return false // no problems
  },
  plateNumber: value => {
    if (!value || !value.trim().length) {
      return "Please enter a valid plate number."
    }
    return false
  },
  courtFilter: value => {
    if (!value) return 'Please select your court region.'
    return false
  },
  selectedTrafficTicket: value => {
    if (!value) return 'Please select a traffic ticket to fight.'
    return false
  },
  zipCode: value => {
    if (value === '') {
      return 'Please enter a valid US Zip Code'
    }
    const usZipCodePattern = /^(^\d{5}$)|(^\d{5}-\d{4}$)$/
    if (!usZipCodePattern.test(value))
      return 'Please enter a valid US Zip Code'
    return false
  },
  email: USER_VALIDATIONS.email,
  phoneNumber: USER_VALIDATIONS.phoneNumber
}

export const FETCHER_ASYNC_VALIDATIONS = {
  phoneNumber: async (phoneNumber) => {
    const errorMessage = 'Only US phone numbers are allowed.'
    
    try {
      const { valid } = await validatePhoneNumber(phoneNumber)

      return valid ? false : errorMessage
    } catch (error) {
      return errorMessage
    }
  }
}
